import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { globalReset } from '@/store/actions';

import {
    DEFAULT_INITIAL_MESSAGE,
    DEFAULT_PROMPT,
    DEFAULT_SPEECH_RECOGNITION_LANGUAGE_CODE,
    DEFAULT_VOICE,
} from '@/3d-lib/constants';
import {
    ChatMessage,
    ChatState,
    CurrentSoundRef,
    HumanoidRef,
} from '@/3d-lib/types';

import { SettingsType } from '@/components/common/AIConversation/components/AvatarSetting/types';
import { DEFAULT_MODEL } from '@/components/common/AIConversation/components/AvatarSetting/constant';

import { setIsBossPlayAudio } from '@/store/slices/commonSlice';
import store from '@/store';

// Defining the localStorage key globally
export const PRIVACY_POLICY_ACCEPTED_KEY = 'hasAcceptedPrivacyPolicy';

// State Interface
interface SoundController {
    isVolumeOn: boolean;
}

interface AvatarState {
    soundController: SoundController;
    hasAcceptedPrivacyPolicy: boolean;
    initErrorMessage: string;
    isLoading: boolean;
    lastLLMResponse: string;
    chatState: ChatState;
    aboutModalIsOpen: boolean;
    settingsModalIsOpen: boolean;
    settings: SettingsType;
}

const initialState: AvatarState = {
    soundController: {
        isVolumeOn: true,
    },
    hasAcceptedPrivacyPolicy: true,
    initErrorMessage: '',
    isLoading: true,
    lastLLMResponse: '',
    chatState: {
        messages: [],
        isLoadingMessage: false,
        errorMessage: '',
        charCount: 0,
    },
    aboutModalIsOpen: false,
    settingsModalIsOpen: false,
    settings: {
        voice: DEFAULT_VOICE,
        prompt: DEFAULT_PROMPT,
        initialMessage: DEFAULT_INITIAL_MESSAGE,
        speechRecognitionLanguageCode: DEFAULT_SPEECH_RECOGNITION_LANGUAGE_CODE,
        // XXX: model is null at the beginning. Because we will first check the URL for a model.
        // If we loaded a model here different from the URL model, we'd need to load 2 models at startup.
        model: DEFAULT_MODEL,
    },
};

const avatarSlice = createSlice({
    name: 'avatar',
    initialState,
    reducers: {
        toggleVolume: (state) => {
            state.soundController.isVolumeOn = !state.soundController.isVolumeOn;
        },
        setAcceptedPrivacyPolicy: (state, action: PayloadAction<boolean>) => {
            state.hasAcceptedPrivacyPolicy = action.payload;
            // Set the value in localStorage when action is triggered
            localStorage.setItem(PRIVACY_POLICY_ACCEPTED_KEY, action.payload ? 'true' : 'false');
        },
        setInitErrorMessage: (state, action: PayloadAction<string>) => {
            state.initErrorMessage = action.payload;
        },
        setIsLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setLastLLMResponse: (state, action: PayloadAction<string>) => {
            state.lastLLMResponse = action.payload;
        },
        toggleAboutModal: (state) => {
            state.aboutModalIsOpen = !state.aboutModalIsOpen;
        },
        toggleSettingsModal: (state) => {
            state.settingsModalIsOpen = !state.settingsModalIsOpen;
        },
        setChatState: (state, action: PayloadAction<ChatState>) => {
            state.chatState = action.payload;
        },
        updateChatState: (
            state,
            action: PayloadAction<{
                newMessage?: ChatMessage;
                isLoadingMessage?: boolean;
                errorMessage?: string;
                textAreaValue?: string;
                abortController?: AbortController;
            }>,
        ) => {
            const { newMessage, isLoadingMessage, errorMessage, textAreaValue, abortController } = action.payload;

            if (textAreaValue !== undefined) {
                state.chatState.charCount = textAreaValue.length;
            }

            const lastMessageRole = state.chatState.messages?.at(-1)?.role;

            if (lastMessageRole === 'assistant' && newMessage?.role === 'assistant') {
                if (newMessage !== undefined) {
                    state.chatState.messages[state.chatState.messages.length - 1] = newMessage;

                    // If a server message is done loading, set lastLLMResponse.
                    if (!isLoadingMessage && !errorMessage) {
                        state.lastLLMResponse = newMessage.content;
                    }
                }
            } else if (newMessage !== undefined) {
                state.chatState.errorMessage = '';
                state.chatState.messages.push(newMessage);
            }

            if (isLoadingMessage !== undefined) {
                state.chatState.isLoadingMessage = isLoadingMessage;
            }

            if (errorMessage !== undefined) {
                state.chatState.errorMessage = errorMessage;
            }

            if (abortController !== undefined) {
                state.chatState.abortController = abortController;
            }
        },
        setSettings: (state, action: PayloadAction<SettingsType>) => {
            const settings = action.payload;

            if (settings.initialMessage !== state.settings.initialMessage && state.chatState.messages.length > 0) {
                // If a new initial message is set, update the chat history.
                // Replace first message with the new initial message.
                state.chatState.messages[0].content = settings.initialMessage;
            }

            state.settings = settings;
        },
        clearChatState: (state) => {
            state.chatState = {
                messages: [],
                isLoadingMessage: false,
                errorMessage: '',
                charCount: 0,
            };
            state.lastLLMResponse = '';
        },
        resetAvatar: (state) => {
            Object.assign(state, initialState);
        },
    },
    extraReducers: (builder) => {
        builder
            // Global reset action - clears all avatar state
            .addCase(globalReset, (state) => {
                Object.assign(state, initialState);
            });
    },
});

// Audio handling utilities (side effects - not in Redux state)
// These should be called from components with the necessary refs
export const handleSound = (payload: {
    audio: HTMLAudioElement;
    currentSoundRef: CurrentSoundRef;
    humanoidRef: HumanoidRef;
    name?: string;
    isVolumeOn: boolean;
}) => {
    const { audio, currentSoundRef, humanoidRef, name, isVolumeOn } = payload;

    currentSoundRef.current?.pause();
    humanoidRef.current?.talkAnimationEnd('Received new audio');
    store.dispatch(
        setIsBossPlayAudio({
            isBossPlayAudio: false,
            namePlayAudio: '',
        }),
    );

    if (!audio) {
        return;
    }

    if (!isVolumeOn) {
        audio.volume = 0;
    }

    const onAudioEnd = () => {
        console.log('Audio ended');
        humanoidRef.current?.talkAnimationEnd('onAudioEnd');
        store.dispatch(
            setIsBossPlayAudio({
                isBossPlayAudio: false,
                namePlayAudio: '',
            }),
        );
    };

    // XXX: pause event is emitted after the pause() method is called or BEFORE an ended or seeking event
    // newAudio.addEventListener("pause", onAudioEnd);
    audio.addEventListener('ended', onAudioEnd);

    audio
        .play()
        .then(() => {
            store.dispatch(
                setIsBossPlayAudio({
                    isBossPlayAudio: true,
                    namePlayAudio: name || '',
                }),
            );
            console.log(' Audio is playing');
        })
        .catch((e) => {
            // ignore as direct action
            console.log('Audio is not playing', e);
        });

    currentSoundRef.current = audio;
    humanoidRef.current?.talkAnimationStart();
};

export const handlePauseSound = (payload: { currentSoundRef: CurrentSoundRef; humanoidRef: HumanoidRef }) => {
    const { currentSoundRef, humanoidRef } = payload;
    currentSoundRef.current?.pause();
    humanoidRef.current?.talkAnimationEnd('Paused sound');
    store.dispatch(
        setIsBossPlayAudio({
            isBossPlayAudio: false,
            namePlayAudio: '',
        }),
    );
};

export const {
    toggleVolume,
    setAcceptedPrivacyPolicy,
    setInitErrorMessage,
    setIsLoading,
    setLastLLMResponse,
    toggleAboutModal,
    toggleSettingsModal,
    setChatState,
    updateChatState,
    setSettings,
    clearChatState,
    resetAvatar,
} = avatarSlice.actions;

export default avatarSlice.reducer;
